import riskConsultImage from "@/assets/vaginal-tightening/symptoms/non_surgical_options.jpg.asset.json";
import cgVaginalTightening from "@/assets/cosmetic-gyn-cards/Vaginal_Tightening.webp.asset.json";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { HerniaSurgeryCostGurgaonPage } from "@/components/HerniaSurgeryCostGurgaonPage";
import { herniaCostCity, herniaCostFaqs } from "@/lib/hernia-city-lp";
import { herniaHospitalCityFaqs } from "@/lib/hernia-hospital-city-lp";
import { herniaTreatmentCityFaqs } from "@/lib/hernia-treatment-city-lp";
import { PageShell } from "@/components/PageShell";
import {
  HymenoplastyAgraPage,
  hymenoplastyAgraFaqs,
  hymenoplastyGurgaonFaqs,
  hymenoplastyHaridwarFaqs,
} from "@/components/HymenoplastyAgraPage";
import { LaserTighteningCostPage } from "@/components/LaserTighteningCostPage";
import { PilesDoctorCityPage } from "@/components/PilesDoctorCityPage";
import { PilesHospitalCityPage } from "@/components/PilesHospitalCityPage";
import { PilesSurgeryCostAgraPage } from "@/components/PilesSurgeryCostAgraPage";
import { PilesSurgeryCostGurgaonPage } from "@/components/PilesSurgeryCostGurgaonPage";
import { PilesSurgeryCostHaridwarPage } from "@/components/PilesSurgeryCostHaridwarPage";
import {
  PilesTreatmentCityPage,
  PILES_TREATMENT_CITIES,
  type PilesTreatmentCity,
} from "@/components/PilesTreatmentCityPage";
import {
  HymenoplastyAgraProcedurePage,
  hymenoplastyAgraProcedureFaqs,
  hymenoplastyGurgaonProcedureFaqs,
  hymenoplastyHaridwarProcedureFaqs,
} from "@/components/HymenoplastyAgraProcedurePage";
import { TreatmentPage, buildContent, type TreatmentLoaderData } from "@/routes/treatments.$slug";
import { contact, findTreatmentBySlug, specialties, type TreatmentEntry } from "@/lib/healic-data";

const SITE_URL = "https://healic.in";

// City slug -> display + SEO city phrase
const CITY_MAP: Record<string, { display: string; seoCity: string }> = {
  gurgaon: { display: "Gurgaon", seoCity: "Gurgaon" },
  gurugram: { display: "Gurugram", seoCity: "Gurugram" },
  noida: { display: "Noida", seoCity: "Noida & Ghaziabad" },
  ghaziabad: { display: "Ghaziabad", seoCity: "Ghaziabad" },
  haridwar: { display: "Haridwar", seoCity: "Haridwar" },
  agra: { display: "Agra", seoCity: "Agra" },
  // Muzaffarnagar is restricted to commissioned pages only (see buildParsed).
  muzaffarnagar: { display: "Muzaffarnagar", seoCity: "Muzaffarnagar" },
};

// Topic keyword -> existing treatment slug (treatments.$slug page)
const TOPIC_MAP: Record<string, string> = {
  piles: "hemorrhoids-piles",
  hemorrhoids: "hemorrhoids-piles",
  hernia: "hernia-repair",
  gallbladder: "gallbladder-cholecystectomy",
  gallstone: "gallbladder-cholecystectomy",
  circumcision: "circumcision",
  varicose: "varicose-veins",
  lipoma: "lipoma-removal",
  gynecomastia: "gynecomastia-surgery",
  gynecomestia: "gynecomastia-surgery", // common misspelling
  gynaecomastia: "gynecomastia-surgery",
  "man-boobs": "gynecomastia-surgery",
  fissure: "anal-fissure",
  fistula: "anal-fistula",
  delivery: "c-section-and-normal-delivery",
  "normal-delivery": "c-section-and-normal-delivery",
  "c-section": "c-section-and-normal-delivery",
  hysterectomy: "hysterectomy-surgery",
  "ovarian-cyst": "ovarian-cystectomy",
  "knee-replacement": "joint-replacement",
  "hip-replacement": "joint-replacement",
  "joint-replacement": "joint-replacement",
  fracture: "forearm-plating",
  "carpal-tunnel": "carpal-tunnel-release",
  "trigger-finger": "trigger-finger-surgery",
  "kidney-stone": "pcnl-surgery",
  pcnl: "pcnl-surgery",
  ureteroscopy: "ureteroscopy",
  prostate: "prostate-surgery",
  turp: "prostate-surgery",
  turbt: "turbt",
  tonsillectomy: "tonsillectomy",
  tonsil: "tonsillectomy",
  septoplasty: "septoplasty",
  adenoidectomy: "adenoidectomy",
  "nasal-bone": "nasal-bone-repair",
  mastoidectomy: "mastoidectomy",
  tympanoplasty: "tympanoplasty",
  cataract: "cataract-surgery",
  vitrectomy: "vitrectomy",
  "d-c": "dandc-mtp-procedures",
  "d-c-dilation-and-curettage": "dandc-mtp-procedures",
  "dilation-and-curettage": "dandc-mtp-procedures",
  dnc: "dandc-mtp-procedures",
  mtp: "dandc-mtp-procedures",
  miscarriage: "dandc-mtp-procedures",
  abortion: "dandc-mtp-procedures",
  // Cosmetic & functional gynaecology
  "vaginal-tightening": "vaginal-tightening",
  "vagainal-tightening": "vaginal-tightening", // common misspelling
  vaginoplasty: "vaginoplasty",
  labiaplasty: "labiaplasty",
  "vaginal-prp": "vaginal-prp",
  prp: "vaginal-prp",
  "stress-urinary-incontinence": "stress-urinary-incontinence",
  sui: "stress-urinary-incontinence",
  incontinence: "stress-urinary-incontinence",
  "genital-warts": "genital-warts",
  warts: "genital-warts",
  hymenoplasty: "hymen-reconstruction",
  "hymen-reconstruction": "hymen-reconstruction",
};

type PageType =
  | "treatment"
  | "symptoms-causes"
  | "conditions"
  | "surgery-procedure"
  | "procedure"
  | "hospital"
  | "doctor"
  | "doctors"
  | "surgery-cost"
  | "cost"
  | "zsr"
  | "stapler"
  | "laser"
  | "risk"
  | "surgery";

// Order matters: longer suffixes first so "surgery-cost" wins over "cost".
const SUFFIX_PAGE_TYPES: PageType[] = [
  "symptoms-causes",
  "conditions",
  "surgery-procedure",
  "surgery-cost",
  "treatment",
  "procedure",
  "hospital",
  "doctors",
  "doctor",
  "cost",
  "risk",
  "zsr",
  "stapler",
  "laser",
  "surgery",
];

// Page types that can also appear as a PREFIX (e.g. hospital-hymenoplasty, doctor-hymenoplasty)
const PREFIX_PAGE_TYPES: PageType[] = ["hospital", "doctors", "doctor", "cost"];
// Techniques that can appear as a PREFIX before topic (e.g. laser-vaginal-tightening-cost)
const PREFIX_TECHNIQUES: PageType[] = ["laser", "stapler", "zsr"];

interface Parsed {
  treatment: TreatmentEntry;
  city: { slug: string; display: string; seoCity: string };
  pageType: PageType;
  topicSlug: string;
  technique?: PageType;
}

function buildParsed(
  cityKey: string,
  city: { display: string; seoCity: string },
  topic: string,
  pageType: PageType,
  technique?: PageType,
): Parsed | null {
  // Muzaffarnagar joins the surgery-city matrix only where localized content is
  // explicitly commissioned — the piles pages and hernia surgery cost page.
  if (cityKey === "muzaffarnagar" && !(!technique && ((topic === "piles" && (pageType === "treatment" || pageType === "surgery-cost" || pageType === "hospital" || pageType === "doctor")) || (topic === "hernia" && (pageType === "treatment" || pageType === "surgery-cost" || pageType === "hospital"))))) {
    return null;
  }
  const treatment = findTreatmentBySlug(TOPIC_MAP[topic]);
  if (!treatment) return null;
  return {
    treatment,
    city: { slug: cityKey, display: city.display, seoCity: city.seoCity },
    pageType,
    topicSlug: topic,
    technique,
  };
}

function parseSlug(citySlug: string, topicCombo: string): Parsed | null {
  const cityKey = citySlug.toLowerCase();
  const city = CITY_MAP[cityKey];
  if (!city) return null;
  // Strip optional "best-" prefix (e.g. best-d-c-doctor → d-c-doctor)
  let combo = topicCombo.toLowerCase().replace(/^best-/, "");
  // Handle "-surgery-risk" by collapsing to "-risk"
  combo = combo.replace(/-surgery-risk$/, "-risk");

  // 1. Technique prefix: laser-<rest>
  let technique: PageType | undefined;
  for (const tech of PREFIX_TECHNIQUES) {
    if (combo.startsWith(`${tech}-`)) {
      technique = tech;
      combo = combo.slice(tech.length + 1);
      break;
    }
  }

  // 2. PageType PREFIX: hospital-<topic>, doctor-<topic>
  for (const pt of PREFIX_PAGE_TYPES) {
    if (combo.startsWith(`${pt}-`)) {
      const topic = combo.slice(pt.length + 1);
      if (TOPIC_MAP[topic]) return buildParsed(cityKey, city, topic, pt, technique);
    }
  }

  // 3. PageType SUFFIX: <topic>-<pageType>
  for (const pt of SUFFIX_PAGE_TYPES) {
    const suffix = `-${pt}`;
    if (combo.endsWith(suffix)) {
      const topic = combo.slice(0, -suffix.length);
      if (TOPIC_MAP[topic]) return buildParsed(cityKey, city, topic, pt, technique);
    }
  }

  // 4. Bare topic → default treatment page (still respects technique prefix)
  if (TOPIC_MAP[combo]) {
    return buildParsed(cityKey, city, combo, technique ?? "treatment", technique);
  }

  return null;
}

const TOPIC_DISPLAY: Record<string, string> = {
  gynecomastia: "Gynecomastia",
  gynecomestia: "Gynecomastia",
  gynaecomastia: "Gynecomastia",
  "man-boobs": "Gynecomastia (Man Boobs)",
  "d-c": "D&C",
  "d-c-dilation-and-curettage": "D&C (Dilation & Curettage)",
  "dilation-and-curettage": "Dilation & Curettage",
  dnc: "D&C",
  mtp: "MTP (Medical Termination of Pregnancy)",
  miscarriage: "Miscarriage (D&C)",
  abortion: "Abortion (MTP)",
  hymenoplasty: "Hymenoplasty (Hymen Reconstruction)",
  "hymen-reconstruction": "Hymen Reconstruction (Hymenoplasty)",
  "vagainal-tightening": "Vaginal Tightening",
  "vaginal-prp": "Vaginal PRP",
  prp: "Vaginal PRP",
  sui: "Stress Urinary Incontinence",
  incontinence: "Stress Urinary Incontinence",
  warts: "Genital Warts",
};

function topicName(topicSlug: string): string {
  if (TOPIC_DISPLAY[topicSlug]) return TOPIC_DISPLAY[topicSlug];
  return topicSlug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function pageLabel(pageType: PageType): string {
  switch (pageType) {
    case "treatment":
      return "Treatment";
    case "symptoms-causes":
      return "Symptoms & Causes";
    case "conditions":
      return "Conditions & Grades";
    case "surgery":
      return "Surgery";
    case "surgery-procedure":
    case "procedure":
      return "Surgery Procedure";
    case "hospital":
      return "Hospital";
    case "doctor":
    case "doctors":
      return "Doctors";
    case "surgery-cost":
    case "cost":
      return "Surgery Cost";
    case "risk":
      return "Surgery Risks";
    case "zsr":
      return "ZSR Stapler";
    case "stapler":
      return "Stapler";
    case "laser":
      return "Laser";
  }
}

function techniqueName(pageType: PageType | undefined): string {
  switch (pageType) {
    case "zsr":
      return "ZSR Stapler";
    case "stapler":
      return "Stapler";
    case "laser":
      return "Laser";
    default:
      return "";
  }
}

function withTech(tech: string, base: string): string {
  return tech ? `${tech} ${base}` : base;
}

function buildHeading(
  topic: string,
  pageType: PageType,
  city: string,
  technique?: PageType,
): string {
  const tech = techniqueName(technique);
  switch (pageType) {
    case "treatment":
      return `${withTech(tech, topic)} Treatment in ${city}`;
    case "symptoms-causes":
      return `${topic} Symptoms & Causes in ${city}`;
    case "conditions":
      return `${topic} Conditions, Grades & Types in ${city}`;
    case "surgery":
      return `${withTech(tech, topic)} Surgery in ${city}`;
    case "surgery-procedure":
    case "procedure":
      return `${withTech(tech, topic)} Surgery Procedure in ${city}`;
    case "hospital":
      return `Best ${withTech(tech, topic)} Hospital in ${city}`;
    case "doctor":
    case "doctors":
      return `Best ${withTech(tech, topic)} Doctors in ${city}`;
    case "surgery-cost":
    case "cost":
      return `${withTech(tech, topic)} Surgery Cost in ${city}`;
    case "risk":
      return `${withTech(tech, topic)} Surgery Risks & Side Effects in ${city}`;
    case "zsr":
      return `ZSR Stapler ${topic} in ${city}`;
    case "stapler":
      return `Stapler ${topic} in ${city}`;
    case "laser":
      return `Laser ${topic} in ${city}`;
  }
}

function buildCityFaqs(
  topic: string,
  pageType: PageType,
  city: string,
  seoCity: string,
  technique?: PageType,
): { q: string; a: string }[] {
  const tech = techniqueName(technique);
  const techTopic = tech ? `${tech} ${topic}` : topic;
  const topicLower = topic.toLowerCase();
  const techTopicLower = techTopic.toLowerCase();
  const b = (s: string) => `<strong>${s}</strong>`;
  const isCost = pageType === "cost" || pageType === "surgery-cost";
  const isDoctor = pageType === "doctor" || pageType === "doctors";
  const isHospital = pageType === "hospital";
  const isRisk = pageType === "risk";

  const faqs: { q: string; a: string }[] = [];

  // 1. Cost — always useful, becomes primary on cost pages
  faqs.push({
    q: `What is the ${techTopic} ${b("cost in " + city)}?`,
    a: `The ${techTopicLower} ${b("cost in " + seoCity)} at ${b("Healic " + city)} typically starts from an affordable fixed-price package and varies with the grade of disease, technique used, anaesthesia and room category. We publish transparent packages with ${b("cashless insurance")} support and ${b("0% EMI")} — no last-minute surprises. Call us for a free personalised estimate.`,
  });

  // 2. Best hospital / doctor angle
  if (isDoctor) {
    faqs.push({
      q: `Who is the best ${topic} doctor ${b("in " + city)}?`,
      a: `${b("Healic " + city)} has a panel of senior ${topicLower} specialists with 10+ years of experience performing ${techTopicLower} procedures for patients across ${b(seoCity)}. Every patient is personally evaluated and operated on by a consultant — never a trainee.`,
    });
  } else {
    faqs.push({
      q: `Which is the best hospital for ${techTopic} ${b("in " + city)}?`,
      a: `${b("Healic Multispeciality Hospital, " + city)} is among the most trusted names in ${b(seoCity)} for ${techTopicLower} — modular OTs, ICU back-up, senior consultants and transparent fixed-price packages make it a preferred choice for patients from ${seoCity} and nearby.`,
    });
  }

  // 3. Is it safe / risky
  faqs.push({
    q: `Is ${techTopic} safe ${b("in " + city)}?`,
    a: `Yes. At ${b("Healic " + city)}, ${techTopicLower} is performed by senior specialists in modular operation theatres with sterile single-use instruments, structured pre-op work-up and post-op monitoring. Complications are rare and are managed proactively with 24×7 in-house support in ${seoCity}.`,
  });

  // 4. Recovery / return to work
  faqs.push({
    q: `How long is the recovery after ${techTopicLower} ${b("in " + city)}?`,
    a: `Most patients undergoing ${techTopicLower} at ${b("Healic " + city)} go home the same day or within 24 hours and return to desk work in ${b("3–5 days")}. Full recovery — including exercise and heavy activity — usually takes 2–3 weeks, guided by a written recovery plan and WhatsApp follow-up from our team in ${seoCity}.`,
  });

  // 5. Insurance / cashless
  faqs.push({
    q: `Is ${topic} ${isCost ? b("cost") : "surgery"} covered by ${b("insurance in " + city)}?`,
    a: `Yes — most medically advised ${topicLower} procedures are covered by eligible health insurance policies. ${b("Healic " + city)} is empanelled with all major TPAs and insurers and provides ${b("cashless")} pre-authorisation, documentation support and ${b("no-cost EMI")} options for patients across ${seoCity}.`,
  });

  // 6. Daycare / admission
  faqs.push({
    q: `Do I need to be admitted for ${techTopicLower} ${b("at Healic " + city)}?`,
    a: `${techTopic} at ${b("Healic " + city)} is a modern ${b("daycare procedure")} — you are admitted in the morning, operated within 1–2 hours and discharged the same evening in most cases. Patients from ${b(seoCity)} avoid overnight stay, saving both time and ${b("cost")}.`,
  });

  // 7. Why choose Healic
  faqs.push({
    q: `Why choose ${b("Healic " + city)} for ${techTopicLower}?`,
    a: `Patients across ${b(seoCity)} choose ${b("Healic Multispeciality Hospital")} for its senior consultants, modular OTs, transparent ${b("fixed-price")} packages, ${b("cashless insurance")}, ${b("0% EMI")}, dedicated care coordinators and honest surgical advice — Healic operates only if you truly need it, with no fazool tests.`,
  });

  // Drop generic slots to keep 6-7 on non-cost/non-risk pages
  if (isRisk) {
    faqs.splice(4, 1); // remove insurance on risk pages, keep risk-focused ones
  }
  if (isHospital) {
    faqs.splice(1, 1); // hospital title already answers "which hospital"; drop dup
  }

  return faqs.slice(0, 7);
}

function buildCityIntro(
  topic: string,
  pageType: PageType,
  city: string,
  seoCity: string,
  specialtyName: string,
  technique?: PageType,
): string {
  const lower = topic.toLowerCase();
  const tech = techniqueName(technique);
  const techLower = tech.toLowerCase();
  const techPrefix = tech ? `${tech.toLowerCase()} ` : "";
  switch (pageType) {
    case "symptoms-causes":
      return `Understand the symptoms and causes of ${lower} — what triggers it, warning signs to watch for, and when to see a specialist. Healic ${city} evaluates patients across ${seoCity} with a senior ${specialtyName.toLowerCase()} consultant, clinical examination and the right investigations before any treatment is advised.`;
    case "conditions":
      return `${topic} presents in different grades and types, and the right treatment depends on which one you have. This page explains the conditions, grades and stages seen at Healic ${city}, and how our ${specialtyName.toLowerCase()} team plans treatment for patients across ${seoCity}.`;
    case "treatment":
      return `Healic ${city} offers advanced ${techPrefix}${lower} treatment for patients across ${seoCity}. Our ${specialtyName.toLowerCase()} team uses minimally invasive techniques, fixed-price packages and cashless insurance, with same-day discharge for most procedures.`;
    case "surgery":
      return `Healic ${city} offers safe, modern ${techPrefix}${lower} surgery for women across ${seoCity} — performed by senior female gynae-surgeons in modular operation theatres with complete privacy, fixed-price packages and cashless insurance.`;
    case "surgery-procedure":
    case "procedure":
      return `This page explains the complete ${techPrefix}${lower} surgery procedure at Healic ${city} — pre-operative work-up, anaesthesia, the surgical technique used, OT duration, discharge and the recovery timeline patients in ${seoCity} can expect.`;
    case "hospital":
      return `Healic ${city} is among the most trusted hospitals in ${seoCity} for ${techPrefix}${lower} — modular operation theatres, ICU back-up, dedicated care coordinators and transparent pricing make us a preferred choice.`;
    case "doctor":
    case "doctors":
      return `Our ${specialtyName.toLowerCase()} team at Healic ${city} performs hundreds of ${techPrefix}${lower} procedures every year. Every patient in ${seoCity} is personally evaluated, counselled and operated on by a senior consultant — never a trainee.`;
    case "surgery-cost":
    case "cost":
      return `${withTech(tech, topic)} surgery cost in ${seoCity} depends on the grade of disease, technique used, anaesthesia and choice of room. Healic ${city} publishes fixed-price packages with cashless insurance and 0% EMI — so there are no last-minute surprises.`;
    case "risk":
      return `Like any surgery, ${techPrefix}${lower} has a small set of risks — bleeding, infection, anaesthesia reaction and rarely recurrence. At Healic ${city} we minimise these for patients across ${seoCity} with senior surgeons, modular OTs, sterile single-use instruments and structured post-op follow-up.`;
    case "zsr":
      return `ZSR stapler ${lower} at Healic ${city} is a modern, sutureless technique preferred by patients across ${seoCity} — single-use disposable stapler, no stitches, minimal bleeding, 15-minute daycare procedure with same-day discharge and quick return to routine.`;
    case "stapler":
      return `Stapler ${lower} at Healic ${city} uses a single-use disposable device for a precise, sutureless procedure — patients across ${seoCity} get less pain, minimal bleeding and a faster recovery compared to the conventional open technique.`;
    case "laser":
      return `${techLower ? `${tech} ` : "Laser "}${lower} at Healic ${city} is a bloodless, daycare procedure using a precision laser beam — patients across ${seoCity} benefit from minimal pain, no stitches in most cases, and same-day discharge.`;
  }
}

/** FAQ set actually rendered on a city landing page (shared by head + component). */
function resolveCityFaqs(d: Parsed): { q: string; a: string }[] {
  const { treatment, city, pageType, topicSlug, technique } = d;
  const herniaCity = herniaCostCity(city.slug);
  if (herniaCity && topicSlug === "hernia" && pageType === "surgery-cost") return herniaCostFaqs(herniaCity);
  const topic = topicName(topicSlug);
  const specialty = specialties.find((s) => s.slug === treatment.specialtySlug)!;
  const baseContent = buildContent(treatment.name, specialty.name, treatment.slug);
  const isLaserCost =
    topicSlug === "vaginal-tightening" &&
    (pageType === "cost" || pageType === "surgery-cost") &&
    technique === "laser" &&
    LASER_CITIES[city.slug];
  if (isLaserCost)
    return city.slug === "agra"
      ? AGRA_LASER_FAQS
      : city.slug === "gurgaon"
        ? GURGAON_LASER_FAQS
        : city.slug === "haridwar"
          ? HARIDWAR_LASER_FAQS
          : laserFaqs(LASER_CITIES[city.slug]!.display);

  if (
    city.slug === "gurgaon" &&
    pageType === "risk" &&
    !technique &&
    (topicSlug === "vagainal-tightening" || topicSlug === "vaginal-tightening")
  ) {
    return GURGAON_RISK_FAQS;
  }
  return [
    ...buildCityFaqs(topic, pageType, city.display, city.seoCity, technique),
    ...baseContent.faqs.slice(0, 2),
  ];
}

/** Natural, non-templated patient testimonials for city landing pages. */
function cityReviews(cityDisplay: string): { name: string; text: string }[] {
  return [
    {
      name: "Priya M.",
      text: `I was quoted one figure over the phone and that is exactly what I paid at Healic ${cityDisplay}. No hidden add-ons at discharge, which honestly was my biggest worry.`,
    },
    {
      name: "Rahul S.",
      text: `The consultant sat with me for a good twenty minutes and answered every question without rushing. I went home the same evening and was back at work in a few days.`,
    },
    {
      name: "Anil K.",
      text: `Clean rooms, polite nursing staff and someone actually called to check on me two days after discharge. That follow-up made a real difference.`,
    },
    {
      name: "Sunita R.",
      text: `What stood out was the privacy. Everything was discussed one-to-one, the paperwork was simple and the insurance desk handled the cashless approval for me.`,
    },
  ];
}

export const Route = createFileRoute("/$city/$topic")({
  loader: ({ params }) => {
    const parsed = parseSlug(params.city, params.topic);
    if (!parsed) throw notFound();
    return parsed;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Healic Multispeciality Hospital" }] };
    const { city, pageType, topicSlug, technique } = loaderData;
    const topic = topicName(topicSlug);
    const label = pageLabel(pageType);
    const tech = techniqueName(technique);
    const techTopic = tech ? `${tech} ${topic}` : topic;
    // Canonical must self-reference the requested URL. Slug parsing normalises
    // aliases (e.g. "-surgery-risk" → "-risk", "best-" prefix), so rebuilding the
    // path from parsed data emitted a canonical pointing at a *different* URL —
    // which made Google drop the advertised sitemap URL as a duplicate.
    // Muzaffarnagar's page uses the exact-case city segment the team requested.
    const citySegment = city.slug === "muzaffarnagar" ? "Muzaffarnagar" : params.city.toLowerCase();
    const path = `/${citySegment}/${params.topic.toLowerCase()}`;
    const url = `${SITE_URL}${path}`;
    const isHymenoplastyCostLp =
      (city.slug === "agra" || city.slug === "gurgaon" || city.slug === "haridwar") &&
      topicSlug === "hymenoplasty" &&
      (pageType === "cost" || pageType === "surgery-cost");
    const lpCity =
      city.slug === "gurgaon" ? "Gurgaon" : city.slug === "haridwar" ? "Haridwar" : "Agra";
    const isHymenoplastyProcedureLp =
      (city.slug === "agra" || city.slug === "gurgaon" || city.slug === "haridwar") &&
      topicSlug === "hymenoplasty" &&
      (pageType === "surgery-procedure" || pageType === "procedure");

    const herniaCity = herniaCostCity(city.slug);
    const isHerniaCityCost = Boolean(herniaCity) && topicSlug === "hernia" && pageType === "surgery-cost";
    const isGurgaonHerniaTreatment = Boolean(herniaCity) && topicSlug === "hernia" && pageType === "treatment";
    const isGurgaonHerniaHospital = pageType === "hospital" && ((city.slug === "gurgaon" && topicSlug === "piles") || (city.slug !== "gurgaon" && Boolean(herniaCity) && topicSlug === "hernia"));
    let title = "";
    let description = "";
    switch (pageType) {
      case "symptoms-causes":
        title = `${topic} Symptoms & Causes in ${city.display} | Healic`;
        description = `${topic} symptoms & causes explained by senior specialists in ${city.seoCity}. Know the signs, triggers and when to consult — Healic ${city.display}.`;
        break;
      case "conditions":
        title = `${topic} Conditions, Grades & Types in ${city.display} | Healic`;
        description = `Types, grades and related conditions of ${topic.toLowerCase()} in ${city.seoCity}. Expert diagnosis and tailored treatment plans at Healic ${city.display}.`;
        break;
      case "treatment":
        title = `${techTopic} Treatment in ${city.display} — Laser & Daycare | Healic`;
        description = `Advanced ${techTopic.toLowerCase()} treatment in ${city.seoCity}. Senior surgeons, daycare procedure, cashless insurance & fixed-price packages at Healic ${city.display}.`;
        break;
      case "surgery":
        title = `${techTopic} Surgery in ${city.display} — Safe & Discreet | Healic`;
        description = `${techTopic} surgery in ${city.seoCity} by senior female gynae-surgeons at Healic ${city.display}. Complete privacy, fixed-price packages & cashless insurance.`;
        break;
      case "surgery-procedure":
      case "procedure":
        title = `${techTopic} Surgery Procedure in ${city.display} — Step-by-Step | Healic`;
        description = `${techTopic} surgery procedure explained — anaesthesia, technique, duration, recovery & discharge at Healic ${city.display}. Book a consultation today.`;
        break;
      case "hospital":
        title = `Best ${techTopic} Hospital in ${city.display} — Healic`;
        description = `Looking for the best ${techTopic.toLowerCase()} hospital in ${city.seoCity}? Healic ${city.display} offers modular OTs, ICU back-up, expert surgeons & cashless insurance.`;
        break;
      case "doctor":
      case "doctors":
        title = `Best ${techTopic} Doctor in ${city.display} — Top Surgeons | Healic`;
        description = `Consult the best ${techTopic.toLowerCase()} doctor in ${city.seoCity}. Senior surgeons at Healic ${city.display} with 10+ years of experience. Book your appointment today.`;
        break;
      case "surgery-cost":
      case "cost":
        title = `${techTopic} Surgery Cost in ${city.display} — Fixed Price | Healic`;
        description = `${techTopic} surgery cost in ${city.seoCity} with fixed-price packages, cashless insurance support and 0% EMI at Healic ${city.display}. Get a free estimate.`;
        break;
      case "risk":
        title = `${techTopic} Surgery Risks & Side Effects in ${city.display} | Healic`;
        description = `Know the risks, side effects & safety of ${techTopic.toLowerCase()} surgery in ${city.seoCity}. Senior surgeons at Healic ${city.display} minimise complications with modular OTs and structured follow-up.`;
        break;
      case "zsr":
      case "stapler":
      case "laser": {
        const t = techniqueName(pageType);
        title = `${t} ${topic} in ${city.display} — Sutureless Daycare | Healic`;
        description = `${t} ${topic.toLowerCase()} at Healic ${city.display} — sutureless, daycare procedure for patients in ${city.seoCity}. Minimal pain, no stitches, same-day discharge.`;
        break;
      }
    }

    if (isHymenoplastyCostLp) {
      title = `Hymenoplasty Surgery Cost in ${lpCity} | Best Doctor`;
      description = `Hymenoplasty surgery cost in ${lpCity}: ₹25,000, inclusive of surgeon's fee and follow-up care. Trusted hospital, experienced doctor, private consultation.`;
    }

    if (isHymenoplastyProcedureLp) {
      title = `Hymenoplasty Treatment in ${lpCity} | Best Gynecologist`;
      description = `Understand the hymenoplasty procedure in ${lpCity}, what the hymen actually is, and why Healic's gynecologist and hospital are trusted for this treatment.`;
    }

    const pilesTreatmentCity =
      topicSlug === "piles" && pageType === "treatment"
        ? ({
            agra: "Agra",
            gurgaon: "Gurgaon",
            haridwar: "Haridwar",
            ghaziabad: "Ghaziabad",
            muzaffarnagar: "Muzaffarnagar",
          } as const)[city.slug as "agra" | "gurgaon" | "haridwar" | "ghaziabad" | "muzaffarnagar"]
        : undefined;
    if (pilesTreatmentCity) {
      const doctor = PILES_TREATMENT_CITIES[pilesTreatmentCity].doctor;
      title = `Piles Treatment in ${pilesTreatmentCity} | Laser Surgery | Healic`;
      description = `Get advanced piles treatment in ${pilesTreatmentCity} with ${doctor}. Learn about laser, stapler and surgical options, recovery, cashless insurance and transparent costs.`;
    }

    const pilesHospitalCity =
      topicSlug === "piles" && pageType === "hospital"
        ? ({ agra: "Agra", gurgaon: "Gurgaon", haridwar: "Haridwar", ghaziabad: "Ghaziabad", muzaffarnagar: "Muzaffarnagar" } as const)[
            city.slug as "agra" | "gurgaon" | "haridwar" | "ghaziabad" | "muzaffarnagar"
          ]
        : undefined;
    if (pilesHospitalCity) {
      const doctor = PILES_TREATMENT_CITIES[pilesHospitalCity].doctor;
      title = `Piles Hospital in ${pilesHospitalCity} | Laser Care | Healic`;
      description = `Visit Healic piles hospital in ${pilesHospitalCity} for laser piles, fissure and fistula care with ${doctor}, cashless insurance and transparent packages.`;
    }

    const pilesDoctorCity =
      topicSlug === "piles" && (pageType === "doctor" || pageType === "doctors")
        ? ({ agra: "Agra", gurgaon: "Gurgaon", haridwar: "Haridwar", ghaziabad: "Ghaziabad", muzaffarnagar: "Muzaffarnagar" } as const)[
            city.slug as "agra" | "gurgaon" | "haridwar" | "ghaziabad" | "muzaffarnagar"
          ]
        : undefined;
    if (pilesDoctorCity) {
      const doctor = PILES_TREATMENT_CITIES[pilesDoctorCity].doctor;
      title = `Piles Doctor in ${pilesDoctorCity} | Laser Care | Healic`;
      description = `Consult ${doctor}, an experienced piles doctor in ${pilesDoctorCity}, for laser piles, fissure and fistula care at Healic. Book a confidential appointment.`;
    }

    // Page-specific overrides (laser vaginal tightening cost — city pages)
    if (
      topicSlug === "vaginal-tightening" &&
      (pageType === "cost" || pageType === "surgery-cost") &&
      technique === "laser" &&
      LASER_CITIES[city.slug]
    ) {
      const lc = LASER_CITIES[city.slug];
      title = lc.metaTitle;
      description = lc.metaDescription;
    }

    // Single-page override: /gurgaon/vagainal-tightening-surgery-risk
    if (
      city.slug === "gurgaon" &&
      pageType === "risk" &&
      !technique &&
      (topicSlug === "vagainal-tightening" || topicSlug === "vaginal-tightening")
    ) {
      title = "Vaginal Tightening Surgery Risks in Gurgaon | Healic";
      description =
        "Considering vaginal tightening in Gurgaon? Learn the real risks, safety measures & recovery — senior surgeons, modular OTs, fixed pricing at Healic.";
    }

    if (isHerniaCityCost) {
      title = `Hernia Surgery Cost in ${city.display} — Fixed Price | Healic`;
      description = `Hernia surgery cost in ${city.display} at Healic — all-inclusive fixed-price packages, cashless insurance and 0% EMI. Transparent hernia treatment cost with a free estimate.`;
    }
    if (isGurgaonHerniaHospital) {
      title = `Best Hospital for Hernia in ${city.display} | Laparoscopic Care | Healic`;
      description = `Hernia care at Healic ${city.display} with ${herniaCity ? PILES_TREATMENT_CITIES[herniaCity].doctor : "Dr. N. K. Mandal"}: laparoscopic and open mesh repair, day-care surgery, fixed-price packages and cashless insurance support.`;
    }
    if (isGurgaonHerniaTreatment) {
      title = `Hernia Treatment in ${city.display} | Laparoscopic Surgery | Healic`;
      description = `Hernia treatment at Healic ${city.display} with ${herniaCity ? PILES_TREATMENT_CITIES[herniaCity].doctor : "Dr. N. K. Mandal"}: laparoscopic and open mesh repair, symptoms, recovery, package information and cashless insurance support.`;
    }
    const pageFaqs = isGurgaonHerniaTreatment && herniaCity ? herniaTreatmentCityFaqs(herniaCity) : isGurgaonHerniaHospital && herniaCity ? herniaHospitalCityFaqs(herniaCity) : isHerniaCityCost && herniaCity ? herniaCostFaqs(herniaCity) : pilesDoctorCity
      ? [
          {
            q: `Who treats piles at Healic ${pilesDoctorCity}?`,
            a: `${PILES_TREATMENT_CITIES[pilesDoctorCity].doctor} provides assessment and surgical care for piles, fissure, fistula and pilonidal sinus at Healic ${pilesDoctorCity}.`,
          },
          {
            q: "Is laser piles surgery painful?",
            a: "Laser treatment usually causes less discomfort than conventional open surgery. Suitability depends on the grade of piles and the surgeon's assessment.",
          },
          {
            q: "Will I need to stay overnight?",
            a: "Many laser piles procedures are completed as day care. The surgeon confirms discharge timing after assessing your condition and overall health.",
          },
          {
            q: "Does Healic support cashless insurance?",
            a: "Healic supports cashless admission with eligible insurers and TPAs. The care team checks policy eligibility before admission.",
          },
        ]
      : isHymenoplastyProcedureLp
      ? lpCity === "Gurgaon"
        ? hymenoplastyGurgaonProcedureFaqs
        : lpCity === "Haridwar"
          ? hymenoplastyHaridwarProcedureFaqs
          : hymenoplastyAgraProcedureFaqs
      : isHymenoplastyCostLp
      ? lpCity === "Gurgaon"
        ? hymenoplastyGurgaonFaqs
        : lpCity === "Haridwar"
          ? hymenoplastyHaridwarFaqs
          : hymenoplastyAgraFaqs
      : resolveCityFaqs(loaderData);

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { name: "geo.placename", content: city.display },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_IN" },
        { property: "og:site_name", content: "Healic Multispeciality Hospital" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: title,
            headline: title,
            description,
            url,
            inLanguage: "en-IN",
            isPartOf: {
              "@type": "WebSite",
              name: "Healic Multispeciality Hospital",
              url: SITE_URL,
            },
            about: { "@type": "MedicalProcedure", name: isGurgaonHerniaHospital ? "Hernia Repair" : techTopic },
            audience: { "@type": "Patient" },
            specialty:
              topicSlug === "piles" || topicSlug === "hernia"
                ? "https://schema.org/Surgical"
                : "https://schema.org/Gynecologic",
            publisher: {
              "@type": "MedicalOrganization",
              name: "Healic Multispeciality Hospital",
              url: SITE_URL,
            },
            ...(isHymenoplastyCostLp || isHymenoplastyProcedureLp
              ? {
                  mainEntity: {
                    "@type": "MedicalProcedure",
                    name: "Hymenoplasty",
                    procedureType: "Surgical",
                    bodyLocation: "Hymen",
                  },
                  reviewedBy: {
                    "@type": "Physician",
                    name:
                      lpCity === "Gurgaon"
                        ? "Dr. Pragya Richa"
                        : lpCity === "Haridwar"
                          ? "Dr. Deepika Yadav"
                          : "Dr. Kanika Bhadhkaria",
                    medicalSpecialty: "Obstetrics and Gynaecology",
                    worksFor: {
                      "@type": "Hospital",
                      name: `Healic Multispeciality Hospital, ${lpCity}`,
                    },
                  },
                }
              : {}),
            ...(topicSlug === "piles" &&
            ((city.slug === "agra" && pageType === "surgery-cost") || pilesTreatmentCity || pilesHospitalCity || pilesDoctorCity)
              ? {
                  reviewedBy: {
                    "@type": "Physician",
                    name: pilesTreatmentCity
                      ? PILES_TREATMENT_CITIES[pilesTreatmentCity].doctor
                      : pilesHospitalCity
                        ? PILES_TREATMENT_CITIES[pilesHospitalCity].doctor
                        : pilesDoctorCity
                          ? PILES_TREATMENT_CITIES[pilesDoctorCity].doctor
                        : "Dr. Amit Kumar",
                    medicalSpecialty: "General Surgery",
                    worksFor: {
                      "@type": "Hospital",
                      name: `Healic Multispeciality Hospital, ${pilesTreatmentCity ?? pilesHospitalCity ?? pilesDoctorCity ?? "Agra"}`,
                    },
                  },
                }
              : {}),
          }),
        },
        ...(pageFaqs.length
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: pageFaqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }),
              },
            ]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: city.display,
                item: `${SITE_URL}/${city.slug}`,
              },
              { "@type": "ListItem", position: 2, name: `${topic} ${label}`, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Page not found</h1>
        <Link to="/surgeries" className="mt-6 inline-block text-accent">
          Browse all surgeries →
        </Link>
      </div>
    </PageShell>
  ),
  component: CityTopicPage,
});

function CityTopicPage() {
  const { treatment, city, pageType, topicSlug, technique } = Route.useLoaderData();
  const treatmentCity = herniaCostCity(city.slug);
  if (treatmentCity && topicSlug === "hernia" && pageType === "treatment") {
    return <HerniaSurgeryCostGurgaonPage city={treatmentCity} treatment />;
  }
  if (city.slug === "gurgaon" && topicSlug === "piles" && pageType === "hospital") {
    return <HerniaSurgeryCostGurgaonPage hospital />;
  }
  const hospitalCity = herniaCostCity(city.slug);
  if (city.slug !== "gurgaon" && hospitalCity && topicSlug === "hernia" && pageType === "hospital") {
    return <HerniaSurgeryCostGurgaonPage city={hospitalCity} hospital />;
  }
  if (topicSlug === "piles" && (pageType === "doctor" || pageType === "doctors")) {
    const pilesCity = ({
      agra: "Agra",
      gurgaon: "Gurgaon",
      haridwar: "Haridwar",
      ghaziabad: "Ghaziabad",
      muzaffarnagar: "Muzaffarnagar",
    } as const)[city.slug as "agra" | "gurgaon" | "haridwar" | "ghaziabad" | "muzaffarnagar"] as PilesTreatmentCity | undefined;
    if (pilesCity) return <PilesDoctorCityPage city={pilesCity} />;
  }
  if (topicSlug === "piles" && pageType === "hospital") {
    const pilesCity = ({
      agra: "Agra",
      gurgaon: "Gurgaon",
      haridwar: "Haridwar",
      ghaziabad: "Ghaziabad",
      muzaffarnagar: "Muzaffarnagar",
    } as const)[city.slug as "agra" | "gurgaon" | "haridwar" | "ghaziabad" | "muzaffarnagar"] as PilesTreatmentCity | undefined;
    if (pilesCity) return <PilesHospitalCityPage city={pilesCity} />;
  }
  if (topicSlug === "piles" && pageType === "treatment") {
    const pilesCity = ({
      agra: "Agra",
      gurgaon: "Gurgaon",
      haridwar: "Haridwar",
      ghaziabad: "Ghaziabad",
      muzaffarnagar: "Muzaffarnagar",
    } as const)[city.slug as "agra" | "gurgaon" | "haridwar" | "ghaziabad" | "muzaffarnagar"] as PilesTreatmentCity | undefined;
    if (pilesCity) return <PilesTreatmentCityPage city={pilesCity} />;
  }
  if (
    (city.slug === "agra" || city.slug === "gurgaon" || city.slug === "haridwar") &&
    topicSlug === "hymenoplasty" &&
    (pageType === "surgery-procedure" || pageType === "procedure")
  ) {
    return (
      <HymenoplastyAgraProcedurePage
        city={
          city.slug === "gurgaon" ? "Gurgaon" : city.slug === "haridwar" ? "Haridwar" : "Agra"
        }
      />
    );
  }
  if (
    (city.slug === "agra" || city.slug === "gurgaon" || city.slug === "haridwar") &&
    topicSlug === "hymenoplasty" &&
    (pageType === "cost" || pageType === "surgery-cost")
  ) {
    return (
      <HymenoplastyAgraPage
        city={
          city.slug === "gurgaon" ? "Gurgaon" : city.slug === "haridwar" ? "Haridwar" : "Agra"
        }
      />
    );
  }
  if (
    (city.slug === "haridwar" || city.slug === "agra" || city.slug === "gurgaon") &&
    topicSlug === "vaginal-tightening" &&
    technique === "laser" &&
    (pageType === "cost" || pageType === "surgery-cost")
  ) {
    return (
      <LaserTighteningCostPage city={city.slug === "agra" ? "Agra" : city.slug === "gurgaon" ? "Gurgaon" : "Haridwar"} />
    );
  }
  if (
    city.slug === "agra" &&
    topicSlug === "piles" &&
    pageType === "surgery-cost"
  ) {
    return <PilesSurgeryCostAgraPage pageType="cost" />;
  }
  if (
    city.slug === "haridwar" &&
    topicSlug === "piles" &&
    pageType === "surgery-cost"
  ) {
    return <PilesSurgeryCostHaridwarPage pageType="cost" />;
  }
  if (
    city.slug === "gurgaon" &&
    topicSlug === "piles" &&
    pageType === "surgery-cost"
  ) {
    return <PilesSurgeryCostGurgaonPage pageType="cost" />;
  }
  if (city.slug === "muzaffarnagar" && topicSlug === "piles" && pageType === "surgery-cost") {
    return <PilesSurgeryCostGurgaonPage pageType="cost" city="Muzaffarnagar" />;
  }
  const herniaCity = herniaCostCity(city.slug);
  if (herniaCity && topicSlug === "hernia" && pageType === "surgery-cost") {
    return <HerniaSurgeryCostGurgaonPage city={herniaCity} />;
  }
  const topic = topicName(topicSlug);
  const specialty = specialties.find((s) => s.slug === treatment.specialtySlug)!;
  const related = specialty.treatments
    .filter((t) => t !== treatment.name)
    .filter((t) => !(topicSlug === "vaginal-tightening" && technique === "laser" && ["Vaginoplasty", "Vaginal PRP"].includes(t)))
    .slice(0, 4);
  const baseContent = buildContent(treatment.name, specialty.name, treatment.slug);

  const heading = buildHeading(topic, pageType, city.display, technique);
  const cityIntro = buildCityIntro(
    topic,
    pageType,
    city.display,
    city.seoCity,
    specialty.name,
    technique,
  );

  const isLaserTighteningCost =
    topicSlug === "vaginal-tightening" &&
    (pageType === "cost" || pageType === "surgery-cost") &&
    technique === "laser";
  const lc = isLaserTighteningCost ? LASER_CITIES[city.slug] : undefined;

  // Single-page override: /gurgaon/vagainal-tightening-surgery-risk
  const isGurgaonTighteningRisk =
    city.slug === "gurgaon" &&
    pageType === "risk" &&
    !technique &&
    (topicSlug === "vagainal-tightening" || topicSlug === "vaginal-tightening");

  const pageHeading = lc
    ? `Laser Vaginal Tightening in ${lc.display} Fixed Price Packages`
    : isGurgaonTighteningRisk
      ? "Vaginal Tightening Surgery: Risks, Safety & What to Expect in Gurgaon"
      : heading;
  const pageIntro = lc
    ? `Wondering what this actually costs before you even pick up the phone? Packages at Healic ${lc.display} start from ${lc.priceFrom}, and the final number depends on the technique you need, whether local or general anaesthesia is involved, and the room category you choose. We put that number in writing before you're admitted — not after — so there's nothing left to negotiate on the day of your procedure.`
    : isGurgaonTighteningRisk
      ? "Every surgery carries some risk, and vaginal tightening is no exception — the main ones are bleeding, infection, a reaction to anaesthesia, and in rare cases, the effects not lasting as long as expected. At Healic Gurgaon, we keep these risks low through experienced surgeons, sterile modular operation theatres, single-use instruments, and close follow-up after your procedure."
      : cityIntro;

  // Override only the strings that appear in the existing treatment layout.
  const overriddenTreatment: TreatmentLoaderData["treatment"] = {
    ...treatment,
    name: pageHeading,
  };
  const cityFaqs = buildCityFaqs(topic, pageType, city.display, city.seoCity, technique);
  const overriddenContent: TreatmentLoaderData["content"] = {
    ...baseContent,
    short: `${pageIntro}`,
    overview: `${pageIntro} ${baseContent.overview}`,
    faqs: lc
      ? laserFaqs(lc.display)
      : isGurgaonTighteningRisk
        ? GURGAON_RISK_FAQS
        : [...cityFaqs, ...baseContent.faqs.slice(0, 2)],
    ...(lc || isGurgaonTighteningRisk ? { methods: LASER_METHODS } : {}),
  };

  const data: TreatmentLoaderData = {
    treatment: overriddenTreatment,
    specialty,
    related,
    content: overriddenContent,
  };

  const laserOverrides = lc
    ? {
        aboutHeading: `About Laser Vaginal Tightening in ${lc.display}`,
        hideAboutSubtitle: true,
        aboutBody: <LaserAbout />,
        symptomsHeading: "Why Patients Choose the Laser Option",
        causesHeading: "Common Causes of Vaginal Laxity",
        methodsHeading: "Treatment Options at Healic",
        methodsIntro:
          "Different patients need different approaches. Our surgeons choose the right method based on your condition, age, and recovery goals — here's what each one actually involves.",
        whyChooseHeading: "Why Choose Healic",
        heroImageAlt: `Laser vaginal tightening procedure at Healic ${lc.display}`,
        hideDoctors: true,
      }
    : isGurgaonTighteningRisk
      ? {
          aboutHeading: "Understanding the Procedure and Its Risks",
          hideAboutSubtitle: true,
          aboutBody: <GurgaonRiskAbout />,
          symptomsHeading: "Why Patients Choose the Laser Option",
          causesHeading: "Common Causes of Vaginal Laxity",
          methodsHeading: "Treatment Options at Healic",
          methodsIntro:
            "Different patients need different approaches. Our surgeons choose the right method based on your condition, age, and recovery goals — here's what each one actually involves.",
          whyChooseHeading: "Why Choose Healic",
          heroImageAlt:
            "Cosmetic gynaecology consultation for vaginal tightening at Healic Gurgaon",
          heroImageSrc: riskConsultImage.url,
        }
      : undefined;

  const laserCostImageAlts = {
    promiseBandAlts: [
      "Surgery only when truly needed",
      "Attentive surgeons and nursing staff",
      "Fixed price confirmed in writing",
      "Planned admission and follow-up",
    ],
    symptomImageAlts: [
      "Non-surgical treatment icon",
      "Improved tone and firmness icon",
      "Enhanced comfort and confidence icon",
      "Better muscle control icon",
    ],
    causeImageAlts: [
      "Childbirth icon",
      "Aging and hormonal changes icon",
      "Menopause icon",
      "Reduced collagen icon",
      "Weight fluctuations icon",
    ],
    journeyStepAlts: [
      "Proper counselling at Healic",
      "Fixed price explanation",
      "Safe treatment process",
      "Structured patient monitoring",
      "Time-bound discharge process",
    ],
  };

  const isAgraLaserCost = Boolean(lc) && city.slug === "agra";
  const agraOverrides = isAgraLaserCost
    ? {
        ...laserCostImageAlts,
        heroBgAlt: "Laser vaginal tightening treatment at Healic Agra",
        heroTitle: "Laser Vaginal Tightening Cost in Agra",
        heroBody: <AgraLaserHero doctor={lc!.doctor} />,
        aboutHeading: "What Shapes Your Package Price",
        hideAboutSubtitle: true,
        aboutBody: <AgraPriceFactors />,
        aboutBackgroundClass: "bg-muted/40",
        aboutHideImage: true,
        symptomsHeading: "Benefits of Vaginal Tightening",
        causesHeading: "Causes of Vaginal Laxity",
        methodsHeading: "Vaginal Tightening Methods at Healic",
        methodsIntro:
          "Different patients need different approaches. Our cosmetic gynecologists choose the right vaginal tightening method for your degree of laxity, age and recovery goals.",
        faqs: AGRA_LASER_FAQS,
      }
    : {};

  const isGurgaonLaserCost = Boolean(lc) && city.slug === "gurgaon";
  const gurgaonOverrides = isGurgaonLaserCost
    ? {
        ...laserCostImageAlts,
        heroBgAlt: "Laser vaginal tightening treatment at Healic Gurgaon",
        heroTitle: "Laser Vaginal Tightening Cost in Gurgaon",
        heroBody: <GurgaonLaserHero />,
        aboutHeading: "Why the Laser Vaginal Tightening Cost Varies",
        hideAboutSubtitle: true,
        aboutBody: <GurgaonPriceFactors />,
        aboutBackgroundClass: "bg-muted/40",
        aboutHideImage: true,
        symptomsHeading: "Benefits of Vaginal Tightening",
        causesHeading: "Causes of Vaginal Laxity",
        methodsHeading: "Treatment Options at Healic",
        methodsIntro:
          "Different patients need different approaches. Our cosmetic gynecologists choose the right vaginal tightening method for your degree of laxity, age and recovery goals.",
        faqs: GURGAON_LASER_FAQS,
        reviews: GURGAON_LASER_REVIEWS,
      }
    : {};

  const isHaridwarLaserCost = Boolean(lc) && city.slug === "haridwar";
  const haridwarOverrides = isHaridwarLaserCost
    ? {
        ...laserCostImageAlts,
        heroBgAlt: "Laser vaginal tightening treatment at Healic Haridwar",
        heroTitle: "Laser Vaginal Tightening Cost in Haridwar",
        heroBody: <HaridwarLaserHero />,
        aboutHeading: "Why the Laser Vaginal Tightening Cost Varies",
        hideAboutSubtitle: true,
        aboutBody: <HaridwarPriceFactors />,
        aboutBackgroundClass: "bg-muted/40",
        aboutHideImage: true,
        symptomsHeading: "Benefits of Vaginal Tightening",
        causesHeading: "Causes of Vaginal Laxity",
        methodsHeading: "Treatment Options at Healic",
        methodsIntro:
          "Different patients need different approaches. Our cosmetic gynecologists choose the right vaginal tightening method for your degree of laxity, age and recovery goals.",
        faqs: HARIDWAR_LASER_FAQS,
      }
    : {};

  const overrides = {
    reviews: cityReviews(city.display),
    ...(laserOverrides ?? {}),
    ...agraOverrides,
    ...gurgaonOverrides,
    ...haridwarOverrides,
  };

  return (
    <TreatmentPage
      data={data}
      overrides={overrides}
      beforeAboutSection={
        isAgraLaserCost ? (
          <AgraLaserAbout />
        ) : isGurgaonLaserCost ? (
          <GurgaonLaserAbout />
        ) : isHaridwarLaserCost ? (
          <HaridwarLaserAbout />
        ) : undefined
      }
      afterAboutSection={
        isAgraLaserCost ? (
          <>
            <AgraLaserSections />
            <LaserSpecialistSection city={lc!} />
          </>
        ) : isGurgaonLaserCost ? (
          <>
            <GurgaonLaserSections />
            <LaserSpecialistSection city={lc!} />
            <GurgaonTrustSection />
          </>
        ) : isHaridwarLaserCost ? (
          <>
            <HaridwarLaserSections />
            <LaserSpecialistSection city={lc!} />
            <HaridwarTrustSection />
          </>
        ) : lc ? (
          <LaserSpecialistSection city={lc} />
        ) : undefined
      }
      beforeFaqSection={isGurgaonTighteningRisk ? <WhenToCallSection /> : undefined}
      extraSection={
        isAgraLaserCost || isGurgaonLaserCost || isHaridwarLaserCost ? (
          <>
            <HubLinkSection />
            <MedicalDisclaimer />
          </>
        ) : undefined
      }
    />
  );
}

type LaserCity = {
  slug: string;
  display: string;
  priceFrom: string;
  metaTitle: string;
  metaDescription: string;
  doctor: {
    slug: string;
    name: string;
    image: string;
    credentials: string;
    experience: string;
    education: string;
  };
};

const LASER_CITIES: Record<string, LaserCity> = {
  agra: {
    slug: "agra",
    display: "Agra",
    priceFrom: "₹15,000–₹20,000",
    metaTitle: "Laser Vaginal Tightening Cost in Agra | Healic",
    metaDescription:
      "Gynecologist-led laser vaginal tightening in Agra from ₹15,000. Fixed pricing, no hidden costs. Book at Healic.",
    doctor: {
      slug: "dr-kanika-bhadhkaria-obstetrics-gynaecology-agra",
      name: "Dr. Kanika Bhadhkaria",
      image: "/__l5e/assets-v1/ea975dc0-edbe-4e9c-b0b6-f9e707bd738c/dr-kanika-bhadhkaria.jpeg",
      credentials: "MBBS, MS (Obstetrics & Gynaecology) — Consultant Obstetrician & Gynaecologist",
      experience: "10+ years of experience",
      education:
        "MS (Obs & Gynae) — M.L.N. Medical College, Prayagraj; MBBS and Senior Residency — S.N. Medical College, Agra.",
    },
  },
  haridwar: {
    slug: "haridwar",
    display: "Haridwar",
    priceFrom: "₹15,000–₹20,000",
    metaTitle: "Laser Vaginal Tightening Cost in Haridwar | Healic",
    metaDescription:
      "Gynecologist-led laser vaginal tightening in Haridwar from ₹15,000. Fixed pricing, no hidden costs. Book at Healic.",
    doctor: {
      slug: "deepika-yadav",
      name: "Dr. Deepika Yadav",
      image:
        "https://cdn.prod.website-files.com/68a9318302af9aa98fc92c3a/693babb903479dedb3fd34d6_6937e6a5b619fec95dd31f9a_Dr.%2520Deepika%2520Yadav.png",
      credentials: "MBBS, DGO (Gold Medalist) — Consultant Obstetrics & Gynaecology",
      experience: "9+ years of experience",
      education:
        "MBBS — G.R. Medical College, Gwalior; DGO (Gold Medalist) — Madhya Pradesh Medical Sciences University, Jabalpur.",
    },
  },
  gurgaon: {
    slug: "gurgaon",
    display: "Gurgaon",
    priceFrom: "₹15,000–₹20,000",
    metaTitle: "Laser Vaginal Tightening Cost in Gurgaon | Vaginal Tightening",
    metaDescription:
      "Know the laser vaginal tightening cost in Gurgaon (₹15,000–₹20,000), procedure, recovery and options. Personalised treatment with Dr. Pragya Richa at Healic — consultation ₹1,000, EMI available.",
    doctor: {
      slug: "pragya-richa",
      name: "Dr. Pragya Richa",
      image:
        "https://cdn.prod.website-files.com/68a9318302af9aa98fc92c3a/693babc7d1447cfc7059abe8_68dff39790fb664bb59ae241_Dr.%2520Pragya%2520Richa.png",
      credentials:
        "MBBS, MS (OBG), FMAS, Diploma in Urogynaecology — Consultant Obstetrician, Gynaecologist & Laparoscopic Surgeon",
      experience: "16+ years of experience",
      education:
        "MS (Obstetrics & Gynaecology) — Aryabhatta Knowledge University, Patna; MBBS — Ranchi University; Diploma in Urogynaecology; FMAS (Fellowship in Minimal Access Surgery).",
    },
  },
  gurugram: {
    slug: "gurugram",
    display: "Gurugram",
    priceFrom: "₹15,000–₹20,000",
    metaTitle: "Laser Vaginal Tightening Cost in Gurugram | Healic",
    metaDescription:
      "Fixed-price laser vaginal tightening (CO₂/Er:YAG) in Gurugram by senior gynaecologists — cashless insurance, 0% EMI. Free consultation.",
    doctor: {
      slug: "pragya-richa",
      name: "Dr. Pragya Richa",
      image:
        "https://cdn.prod.website-files.com/68a9318302af9aa98fc92c3a/693babc7d1447cfc7059abe8_68dff39790fb664bb59ae241_Dr.%2520Pragya%2520Richa.png",
      credentials:
        "MBBS, MS (OBG), FMAS, Diploma in Urogynaecology — Consultant Obstetrician, Gynaecologist & Laparoscopic Surgeon",
      experience: "16+ years of experience",
      education:
        "MS (Obstetrics & Gynaecology) — Aryabhatta Knowledge University, Patna; MBBS — Ranchi University; Diploma in Urogynaecology; FMAS (Fellowship in Minimal Access Surgery).",
    },
  },
};

const LASER_METHODS = [
  {
    name: "Laser Vaginal Tightening (CO₂ / Er:YAG)",
    desc: "A fractional laser is used inside the vaginal canal to create controlled, microscopic zones of thermal energy. This prompts the tissue to respond by producing new collagen and elastin over the following weeks, gradually improving tightness, lubrication and elasticity. It's done under topical anaesthesia in about 15–20 minutes, with no cuts, no stitches and no downtime — most patients return to normal activity the same day. Results typically last 12–18 months and can be extended with a maintenance session. Best suited for mild to moderate laxity.",
  },
  {
    name: "Surgical Vaginoplasty",
    desc: "A surgical procedure that tightens and rebuilds the vaginal muscles and surrounding soft tissues when laxity is moderate to severe. Our cosmetic gynaecologist makes precise, hidden incisions to remove excess vaginal lining, bring together the separated perineal muscles and restore a narrower, more supportive vaginal canal. It is done under regional or general anaesthesia in a sterile operating room, with absorbable sutures so nothing needs removal. Downtime is usually 4–6 weeks, and the results are long-lasting when paired with post-op pelvic floor exercises. Ideal for patients who want a significant, permanent tightening change rather than a quick, non-surgical option.",
  },
  {
    name: "Vaginal PRP Therapy",
    desc: "A regenerative treatment that uses a small amount of your own blood, processed to concentrate platelets and growth factors, then injected into targeted vaginal and clitoral tissues. The PRP signals local cells to repair and rebuild, improving natural lubrication, sensitivity, blood flow and tissue tone. It is often combined with laser sessions to enhance and prolong the tightening effect. The whole visit takes 30–40 minutes, uses local anaesthesia, and carries minimal risk because the material comes from your own body. Results build over 2–3 months and are a popular option for women who want a natural, hormone-free boost in intimate wellness.",
  },
];

function laserFaqs(cityName: string) {
  return [
    {
      q: `What is the Laser Vaginal Tightening cost in ${cityName}?`,
      a: "It really depends on a few things — which technique suits you, whether you need anaesthesia, and the kind of room you'd like to stay in. Rather than give you a vague range, we sit down and work out a fixed number before you're admitted, so nothing changes later. We also handle cashless insurance and 0% EMI if that helps. Easiest way to get an exact figure is to just call us — it takes a few minutes.",
    },
    {
      q: `Which is the best hospital for Laser Vaginal Tightening in ${cityName}?`,
      a: "That's obviously a personal choice, but here's why a lot of patients end up choosing us: senior consultants who've done this many times, proper modular OTs, ICU support if it's ever needed, and pricing that's clear from day one — no vague estimates that grow once you're admitted.",
    },
    {
      q: `Is Laser Vaginal Tightening safe in ${cityName}?`,
      a: "Yes — and safety isn't just a claim we make. Every procedure is done by senior specialists, with sterile single-use instruments and proper pre-op checks beforehand. Complications are genuinely rare, and if anything does need attention, our team is available around the clock, not just during clinic hours.",
    },
    {
      q: `How long is the recovery after laser vaginal tightening in ${cityName}?`,
      a: "Most women are back home the same day, sometimes within 24 hours, and back to regular desk work in about 3 to 5 days. Full recovery — meaning exercise and more strenuous activity — usually takes a couple of weeks. We'll give you a written plan and check in over WhatsApp so you're not left wondering if something feels off.",
    },
    {
      q: `Is Vaginal Tightening cost covered by insurance in ${cityName}?`,
      a: "In most cases, yes, if it's medically advised. We work directly with all the major insurers and TPAs, so we can sort out cashless approval and the paperwork on our end — you don't have to chase it yourself. 0% EMI is also available if you'd rather not deal with insurance at all.",
    },
    {
      q: `Do I need to be admitted for laser vaginal tightening at Healic ${cityName}?`,
      a: "Not really, in the way people usually imagine \u201cadmission.\u201d You come in during the morning, the procedure itself takes an hour or two, and you're back home the same evening. No overnight stay, which most patients are relieved to hear.",
    },
    {
      q: `Why choose Healic ${cityName} for laser vaginal tightening?`,
      a: "Honestly, we try not to oversell it — if you don't need surgery, we'll tell you that too. But if you do go ahead, you're getting experienced consultants, proper facilities, transparent pricing agreed on before admission, and a care coordinator who stays with you through the process instead of leaving you to figure things out alone.",
    },
    {
      q: "Is laser vaginal tightening painful?",
      a: "Not really — most women describe it as a mild warm feeling rather than pain. We use topical anaesthesia, and the whole session is done in about 15 to 20 minutes.",
    },
    {
      q: "How long do the results last?",
      a: "Laser results usually hold up for about 12 to 18 months, and a quick maintenance session can extend that.",
    },
  ];
}

function LaserAbout() {
  return (
    <>
      <p>
        Vaginal laxity is common after childbirth, with age, or after menopause. It's a natural
        change, not something to feel embarrassed about — and it's very treatable.
      </p>
      <p>
        Laser vaginal tightening is the approach we offer at Healic — a non-surgical, in-office
        treatment using CO₂ or Er:YAG energy to stimulate collagen and elastin in the vaginal wall.
        It's best suited for mild to moderate laxity, with no cuts, no stitches and no hospital stay.
      </p>
      <p>
        The laser option is done entirely in-office — no cuts, no stitches, no hospital stay. It
        works by delivering controlled energy into the vaginal wall, which prompts your own tissue
        to rebuild collagen over the following weeks. Most patients notice improved tightness,
        better lubrication, and relief from mild stress incontinence, with no real downtime — you
        walk in, have the procedure, and walk out the same day.
      </p>
      <p>
        Most women come to us wanting something quick and low-risk because they've noticed changes in
        tone and comfort they'd like addressed. Our cosmetic gynaecology team first talks through
        what's actually causing the laxity — a normal delivery, hormonal changes, or menopause — and
        then walks you through the laser tightening plan, with clear pricing and follow-up care built
        into every plan.
      </p>
    </>
  );
}

function LaserSpecialistSection({ city }: { city: LaserCity }) {
  const d = city.doctor;
  return (
    <section className="bg-muted/40 py-12 lg:py-16">
      <div className="mx-auto max-w-5xl px-4 lg:px-8">
        <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
          Meet Your Specialist
        </h2>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Laser vaginal tightening at Healic {city.display} is performed by our senior consultant
          gynaecologist in a fully private, female-led setup.
        </p>

        <div className="mt-6 overflow-hidden rounded-3xl border border-border bg-card shadow-sm sm:flex">
          <div className="bg-doctor-gradient sm:w-56 sm:shrink-0">
            <img
              src={d.image}
              alt={`${d.name}, Consultant Obstetrician & Gynaecologist at Healic ${city.display}`}
              loading="lazy"
              width={448}
              height={448}
              className="h-56 w-full object-cover object-top sm:h-full"
            />
          </div>
          <div className="p-5 sm:p-7">
            <h3 className="font-display text-xl font-bold text-foreground">{d.name}</h3>
            <p className="mt-1 text-sm font-semibold text-accent">{d.credentials}</p>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li>
                <strong className="text-foreground">{d.experience}</strong> in obstetrics,
                gynaecology and cosmetic gynaecology procedures including laser vaginal tightening.
              </li>
              <li>{d.education}</li>
              <li>
                Practising at Healic Multispeciality Hospital, {city.display} — daycare laser
                procedures with complete privacy and confidential consultations.
              </li>
            </ul>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/doctors/$slug"
                params={{ slug: d.slug }}
                className="inline-flex items-center rounded-full bg-accent px-5 py-2 text-sm font-semibold text-accent-foreground"
              >
                View full profile
              </Link>
              <a
                href={`tel:${contact.phoneTel}`}
                className="inline-flex items-center rounded-full border border-border px-5 py-2 text-sm font-semibold text-foreground"
              >
                Book a consultation
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function GurgaonRiskAbout() {
  return (
    <>
      <p>
        At Healic, we offer laser vaginal tightening as a non-surgical route to vaginal
        rejuvenation, depending on how much correction you need. Laser rejuvenation (CO₂ or Er:YAG)
        is a walk-in-walk-out procedure with no cuts or stitches, using heat to stimulate new
        collagen in the vaginal wall. It aims to restore the tone and elasticity that often changes
        after childbirth, ageing, or menopause.
      </p>
      <p>
        Most patients choose laser vaginal tightening because it's quick, low-risk, and needs very
        little downtime — with the added benefit of better tone, comfort, and confidence. During
        your consultation, our cosmetic gynaecology team reviews your history (childbirth, hormonal
        changes, menopause, etc.) and recommends the right laser tightening plan along with clear
        pricing and follow-up care.
      </p>
    </>
  );
}

const WHEN_TO_CALL = [
  "Fever above 100.4°F (38°C)",
  "Heavy bleeding — soaking through a pad in an hour or less",
  "Foul-smelling or unusual discharge",
  "Pain that gets worse instead of better after the first few days",
  "Redness, warmth, or swelling that's spreading",
  "Difficulty or burning while urinating that doesn't improve",
  "Any signs of an allergic reaction (rash, swelling, difficulty breathing)",
];

function WhenToCallSection() {
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-6xl px-4 py-12 lg:px-8 lg:py-16">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-soft sm:p-8">
          <h2 className="font-display text-2xl font-bold sm:text-3xl">
            When to Call Us After Your Procedure
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            Most recoveries are straightforward. But contact your surgeon or the Healic care team
            right away if you notice any of the following:
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {WHEN_TO_CALL.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 rounded-xl bg-primary-soft/40 px-4 py-3 text-sm"
              >
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-[15px] font-medium leading-relaxed">
            When in doubt, call — don't wait it out. Our care coordinators are reachable directly,
            and 24×7 in-house support means you're not left guessing if something feels off.
          </p>
        </div>
      </div>
    </section>
  );
}

const GURGAON_RISK_FAQS: { q: string; a: string }[] = [
  {
    q: "What does vaginal tightening cost in Gurgaon?",
    a: "Pricing depends on the grade of laxity and the number of sessions recommended. At Healic, we quote a fixed package upfront so there are no surprises later, and we accept cashless insurance and 0% EMI. Call us for a personalised estimate based on your case.",
  },
  {
    q: "Which hospital is best for vaginal tightening in Gurgaon?",
    a: "That's a personal decision, but here's what we'd want you to know about us: Healic Gurgaon has ICU back-up, modular operation theatres, and a team of senior consultants who've done this procedure hundreds of times. We'd encourage you to also ask about a hospital's complication rates and post-op support before choosing — not just price.",
  },
  {
    q: "Is vaginal tightening safe?",
    a: "Yes, when done by an experienced surgeon in a proper clinical setting. Complications are uncommon, and the ones that can occur — infection, bleeding, anaesthesia reactions — are manageable with the right precautions. At Healic, every procedure follows a structured pre-op work-up and post-op monitoring plan, with 24×7 support if anything needs attention.",
  },
  {
    q: "How long does recovery take?",
    a: "Most patients go home the same day and are back to desk work within 3–5 days. Full recovery — meaning you can resume exercise and more strenuous activity — usually takes 2–3 weeks. We'll give you a written recovery plan and check in over WhatsApp so you're not guessing along the way.",
  },
  {
    q: "Will I need to stay admitted?",
    a: "No — this is typically a daycare procedure. You come in the morning, the procedure itself takes 1–2 hours, and you're discharged the same evening in most cases. No overnight stay needed.",
  },
  {
    q: "Why do patients choose Healic for this?",
    a: "A few reasons patients tell us: they appreciate that we only recommend surgery when it's actually needed (we don't push unnecessary tests), the pricing is fixed and explained clearly before anything starts, and they feel genuinely looked after by the care team, not just processed through.",
  },
  {
    q: "Does laser vaginal tightening hurt?",
    a: "Not really — most patients describe it as mild warmth rather than pain. It's done under topical anaesthesia and the session itself is over in 15–20 minutes.",
  },
  {
    q: "How long do results last?",
    a: "Laser results typically hold for 12–18 months, and a maintenance session can extend that.",
  },
  {
    q: "Who should avoid vaginal tightening surgery?",
    a: "It's not right for everyone. We generally advise against it — or recommend waiting — for women who are pregnant, have an active pelvic infection, uncontrolled diabetes, or are still within a few months of childbirth. Your surgeon will screen for these during your consultation before recommending any procedure.",
  },
  {
    q: "What happens if there's a complication?",
    a: "While rare, if bleeding, infection, or a slow-healing wound occurs, our team monitors you closely post-op and treats issues immediately — most are manageable with medication or a short follow-up visit rather than further surgery. You'll have a direct line to your care coordinator, not just a general helpline.",
  },
  {
    q: "Can vaginal tightening affect sensation or future childbirth?",
    a: "Done correctly, no — the goal is to restore tone, not scar tissue that limits function. That said, if you're planning to have more children, it's worth discussing timing with your surgeon, since another vaginal delivery can affect the results.",
  },
];

// ---------------------------------------------------------------------------
// Agra-only content: /agra/laser-vaginal-tightening-cost
// ---------------------------------------------------------------------------

function AgraLaserHero({ doctor }: { doctor: LaserCity["doctor"] }) {
  return (
    <>
      <h2 className="font-display text-lg font-semibold text-accent sm:text-xl">
        Transparent, Fixed-Price Packages Starting from ₹15,000
      </h2>
      <p>
        Thinking about laser vaginal tightening and want the real picture before you book a
        consultation? At Healic Agra, cost shouldn&apos;t be the confusing part of this decision.
        Our vaginal tightening packages start from ₹15,000.
      </p>
      <DoctorFactsPanel
        name={doctor.name}
        qualifications={doctor.credentials}
        experience={doctor.experience}
      />
    </>
  );
}

function DoctorFactsPanel({
  name,
  qualifications,
  experience,
  specialization = "Cosmetic & Functional Gynaecology — Laser Vaginal Tightening",
}: {
  name: string;
  qualifications: string;
  experience: string;
  specialization?: string;
}) {
  return (
    <div className="rounded-2xl bg-white/10 p-4 ring-1 ring-white/20 backdrop-blur">
      <p className="text-sm font-semibold">
        Procedures performed by senior, certified gynecological specialists.
      </p>
      <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs uppercase tracking-wide opacity-70">Doctor Name</dt>
          <dd className="font-medium">{name}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide opacity-70">Qualifications</dt>
          <dd className="font-medium">{qualifications}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide opacity-70">Years of Experience</dt>
          <dd className="font-medium">{experience}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide opacity-70">Specialization</dt>
          <dd className="font-medium">{specialization}</dd>
        </div>
      </dl>
    </div>
  );
}

function AgraPriceFactors() {
  return (
    <>
      <p>
        Vaginal tightening treatment isn&apos;t one-size-fits-all, and neither is the price. Your
        final vaginal tightening surgery cost depends on a few real factors:
      </p>
      <ul className="ml-5 list-disc space-y-2">
        <li>Number of sessions recommended for your case</li>
        <li>Degree of laxity and whether a single session is sufficient</li>
        <li>Whether any supportive care or follow-up sessions are advised</li>
      </ul>
      <p>These are confirmed at your consultation — not guessed at over the phone.</p>
    </>
  );
}

function AgraLaserAbout() {
  return (
    <section className="py-12 lg:py-16">
      <div className="mx-auto max-w-5xl px-4 lg:px-8">
        <h2 className="text-center font-display text-2xl font-bold text-foreground sm:text-3xl">
          Laser Vaginal Tightening
        </h2>
        <div className="mt-6 grid items-stretch gap-8 lg:grid-cols-[1.7fr_1fr]">
          <div className="space-y-4 text-sm text-muted-foreground lg:text-base">
            <p>
              Laser vaginal tightening is a same-day, in-clinic treatment — no cuts, no stitches, no
              general anaesthesia required. Using CO₂ or Er:YAG laser technology, controlled energy is
              applied to the vaginal wall, gently heating the tissue to trigger new collagen
              production. As collagen rebuilds over the following weeks, most women notice gradually
              improved firmness, elasticity, and tone.
            </p>
            <p>
              The laser vaginal tightening procedure itself typically takes 15 to 20 minutes. Most
              patients describe the sensation as mild warmth rather than pain, and local numbing is
              used for comfort throughout. There&apos;s no downtime in the traditional sense — most
              women resume normal daily activity, including light exercise, within hours of their
              laser vaginal tightening session.
            </p>
            <p>
              This makes laser vaginal tightening a popular first option for women dealing with mild
              to moderate vaginal laxity, particularly after childbirth or with age-related changes.
              Your gynecologist will help determine during consultation whether laser vaginal
              tightening alone is sufficient for your case, or whether a maintenance plan makes
              more sense.
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border shadow-soft self-start">
            <img
              src={cgVaginalTightening.url}
              alt="Laser vaginal tightening procedure at Healic Multispeciality Hospital"
              width={780}
              height={520}
              loading="lazy"
              decoding="async"
              className="h-full max-h-[340px] w-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}


function AgraLaserSections() {
  return (
    <section className="bg-background py-12 lg:py-16">
      <div className="mx-auto max-w-5xl px-4 lg:px-8">
        <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
          Why See a Lady Gynecologist in Agra for This
        </h2>
        <p className="mt-3 text-sm text-muted-foreground lg:text-base">
          Many women specifically search for a lady gynecologist near them for cosmetic gynaecology
          procedures, and that preference is completely valid — comfort during these consultations
          matters. At Healic, you&apos;ll consult directly with a senior lady gynecologist
          experienced in vaginal tightening and vaginal rejuvenation, not a sales coordinator. If
          you&apos;re comparing gynecologists in Agra for this specifically, ask how many similar
          procedures they&apos;ve performed and whether pricing stays fixed once quoted.
        </p>
      </div>
    </section>
  );
}

const AGRA_LASER_FAQS = [
  {
    q: "How much does laser vaginal tightening cost in Agra?",
    a: "Packages start from ₹15,000, confirmed in writing before your procedure — never estimated after the fact.",
  },
  {
    q: 'Is this the same as what\u2019s sometimes called "virgin tightening surgery"?',
    a: "It's occasionally searched under that term, though it isn't medically accurate. The procedure addresses vaginal tone and elasticity — often relevant after childbirth or with age — and is unrelated to virginity.",
  },
  {
    q: "What's the difference between vaginal tightening and vaginal rejuvenation?",
    a: "Vaginal rejuvenation is the broader category, covering laser treatment and supportive care together. Vaginal tightening usually refers specifically to improving firmness and elasticity, most often via laser.",
  },
  {
    q: "Should I see a lady gynecologist for this procedure?",
    a: "Many patients prefer this, and it's entirely your choice. Our cosmetic gynaecology consultations are led by a senior lady gynecologist with dedicated experience in vaginal tightening and rejuvenation.",
  },
  {
    q: "Is insurance accepted?",
    a: "Cashless insurance applies where medically indicated. 0% EMI is also available.",
  },
  {
    q: "How long is recovery after laser vaginal tightening treatment?",
    a: "Same-day for most patients — you're back to normal activity within hours.",
  },
  {
    q: "How long do results last?",
    a: "Laser results typically last 12–18 months, extendable with a maintenance session.",
  },
];

// ---------------------------------------------------------------------------
// Gurgaon-only content: /gurgaon/laser-vaginal-tightening-cost
// ---------------------------------------------------------------------------

function GurgaonLaserHero() {
  return (
    <>
      <h2 className="text-left font-display text-lg font-semibold text-accent sm:text-xl">
        Transparent, Fixed-Price Packages Starting from ₹15,000
      </h2>
      <p>
        Gurgaon has no shortage of clinics offering vaginal tightening cost estimates that change
        once you&apos;re in the room. At Healic, what we quote is what you pay. Packages start from
        ₹15,000.
      </p>
      <DoctorFactsPanel
        name="Dr. Pragya Richa"
        qualifications="MBBS, MS (Obstetrics & Gynaecology) — Consultant Obstetrician & Gynaecologist"
        experience="10+ years of experience"
      />
    </>
  );
}

function GurgaonLaserAbout() {
  return (
    <section className="py-12 lg:py-16">
      <div className="mx-auto max-w-5xl px-4 lg:px-8">
        <h2 className="text-center font-display text-2xl font-bold text-foreground sm:text-3xl">
          Laser Vaginal Tightening
        </h2>
        <div className="mt-6 grid items-stretch gap-8 lg:grid-cols-[1.7fr_1fr]">
          <div className="space-y-4 text-sm text-muted-foreground lg:text-base">
            <p>
              Laser vaginal tightening treatment is a same-day, in-clinic procedure — no cuts, no
              stitches, no general anaesthesia required. Using CO₂ or Er:YAG laser technology,
              controlled energy is applied to the vaginal wall, gently heating tissue to trigger new
              collagen production. As collagen rebuilds over the following weeks, most women notice
              gradually improved firmness, elasticity, and tone.
            </p>
            <p>
              The laser vaginal tightening treatment itself typically takes 15 to 20 minutes,
              performed with local numbing for comfort. Most patients describe mild warmth rather
              than pain, and resume normal daily activity within hours. This is a non-surgical,
              in-office treatment with no hospital stay and minimal recovery time.
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border shadow-soft self-start">
            <img
              src={cgVaginalTightening.url}
              alt="Laser vaginal tightening procedure at Healic Multispeciality Hospital, Gurgaon"
              width={780}
              height={520}
              loading="lazy"
              decoding="async"
              className="h-full max-h-[340px] w-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function GurgaonPriceFactors() {
  return (
    <>
      <p>
        A lot of Gurgaon clinics quote a low starting figure, then add consultation fees or
        &lsquo;technology charges&rsquo; once you&apos;re already committed. At Healic, the vaginal
        tightening surgery cost quoted upfront already reflects a standard session — anything
        additional is discussed and priced before, not after.
      </p>
      <p>What actually shapes your final number:</p>
      <ul className="ml-5 list-disc space-y-2">
        <li>
          Whether one laser session is enough, or your case benefits from two to three sessions of
          vaginal tightening therapy
        </li>
        <li>Degree of laxity and whether maintenance sessions are advised</li>
        <li>Whether any supportive care is recommended alongside laser treatment</li>
      </ul>
    </>
  );
}

function GurgaonLaserSections() {
  return (
    <section className="bg-background py-12 lg:py-16">
      <div className="mx-auto max-w-5xl px-4 lg:px-8">
        <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
          Finding the Best Gynecologist in Gurgaon for This Procedure
        </h2>
        <div className="mt-3 space-y-4 text-sm text-muted-foreground lg:text-base">
          <p>
            Searching for the best gynecologist in Gurgaon for a cosmetic procedure like this means
            looking past general reviews and asking specific questions: how many laser vaginal
            tightening procedures has this gynecologist surgeon actually performed, will you consult
            directly with them rather than a coordinator, and does the quoted price stay fixed once
            confirmed?
          </p>
          <p>
            At Healic, consultations are led directly by Dr. Pragya Richa, a gynecologist in Gurgaon
            with over a decade of dedicated experience in cosmetic gynaecology — not a general
            practice add-on. Many women specifically look for a lady gynecologist for this kind of
            consultation, and that preference is fully accommodated here.
          </p>
        </div>
      </div>
    </section>
  );
}

/** Gurgaon-specific patient stories for the laser cost page. */
const GURGAON_LASER_REVIEWS = [
  {
    name: "Neha T.",
    text: "The price I was given on my first visit is exactly what appeared on the final bill. After two other consultations in Gurgaon where the number kept moving, that alone convinced me.",
  },
  {
    name: "Ritika S.",
    text: "Dr. Richa explained why laser suited my case better than surgery instead of pushing the costlier option. I was in and out the same morning and back at work the next day.",
  },
  {
    name: "Meher K.",
    text: "Everything happened in a private room with a female team, and no one at reception asked me to explain why I was there. That discretion mattered more than I expected.",
  },
  {
    name: "Divya A.",
    text: "I had a long list of questions and none of them felt rushed. The follow-up call a week later was from the same team, not a random call centre.",
  },
];

function GurgaonTrustSection() {
  const points = [
    "Fixed, written pricing removes the anxiety of an unclear final bill",
    "Direct access to Dr. Richa, rather than a sales coordinator, comes up frequently as a reason patients choose Healic",
    "Same-day recovery for laser treatment makes scheduling around work manageable",
  ];
  return (
    <section className="bg-muted/40">
      <div className="mx-auto max-w-5xl px-4 py-12 lg:px-8 lg:py-14">
        <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
          What Patients Consistently Tell Us
        </h2>
        <ul className="mt-8 grid gap-5 sm:grid-cols-3">
          {points.map((p) => (
            <li
              key={p}
              className="rounded-2xl border border-border bg-card p-5 text-[13px] leading-relaxed text-foreground/80 shadow-soft"
            >
              {p}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const GURGAON_LASER_FAQS = [
  {
    q: "How much does laser vaginal tightening cost in Gurgaon?",
    a: "Packages start from ₹15,000, confirmed in writing before your procedure — never estimated after the fact.",
  },
  {
    q: "Who is the best gynecologist in Gurgaon for this procedure?",
    a: "That depends on your specific needs, but look for a gynecologist surgeon with dedicated cosmetic gynaecology experience, not just general practice. Dr. Pragya Richa has 16+ years of experience in women's healthcare.",
  },
  {
    q: 'Is this the same as what\u2019s sometimes called "virgin tightening surgery"?',
    a: "It's occasionally searched under that term, though it isn't medically accurate. The procedure addresses vaginal tone and elasticity — often relevant after childbirth or with age — unrelated to virginity.",
  },
  {
    q: "What's the difference between vaginal tightening treatment and vaginal rejuvenation?",
    a: "Vaginal rejuvenation is the broader category, covering laser treatment and supportive care together. Vaginal tightening usually refers specifically to improving firmness and elasticity, most often via laser.",
  },
  {
    q: "Should I look specifically for a lady gynecologist in Gurgaon?",
    a: "Many patients prefer this, and it's entirely your choice. Consultations at Healic are led by a senior lady gynecologist experienced in vaginal tightening and rejuvenation.",
  },
  {
    q: "Is insurance accepted?",
    a: "Cashless insurance applies where medically indicated. 0% EMI is also available.",
  },
  {
    q: "How long is recovery after laser vaginal tightening treatment?",
    a: "Same-day for most patients — normal activity resumes within hours.",
  },
  {
    q: "How long do results last?",
    a: "Laser results typically last 12–18 months, extendable with a maintenance session.",
  },
];

// ---------------------------------------------------------------------------
// Haridwar-only content: /haridwar/laser-vaginal-tightening-cost
// ---------------------------------------------------------------------------

function HaridwarLaserHero() {
  return (
    <>
      <h2 className="text-left font-display text-lg font-semibold text-accent sm:text-xl">
        Transparent, Fixed-Price Packages Starting from ₹15,000
      </h2>
      <p>
        Thinking about laser vaginal tightening in Haridwar and want the real picture before you
        book a consultation? At Healic, what we quote is what you pay. Packages start from ₹15,000.
      </p>
      <DoctorFactsPanel
        name="Dr. Deepika Yadav"
        qualifications="MBBS — Consultant Gynaecologist"
        experience="9+ years of experience"
      />
    </>
  );
}

function HaridwarLaserAbout() {
  return (
    <section className="py-12 lg:py-16">
      <div className="mx-auto max-w-5xl px-4 lg:px-8">
        <h2 className="text-center font-display text-2xl font-bold text-foreground sm:text-3xl">
          Laser Vaginal Tightening
        </h2>
        <div className="mt-6 grid items-stretch gap-8 lg:grid-cols-[1.7fr_1fr]">
          <div className="space-y-4 text-sm text-muted-foreground lg:text-base">
            <p>
              Laser vaginal tightening treatment is a same-day, in-clinic procedure — no cuts, no
              stitches, no general anaesthesia required. Using CO₂ or Er:YAG laser technology,
              controlled energy is applied to the vaginal wall, gently heating tissue to trigger new
              collagen production. As collagen rebuilds over the following weeks, most women notice
              gradually improved firmness, elasticity, and tone.
            </p>
            <p>
              The laser vaginal tightening treatment itself typically takes 15 to 20 minutes,
              performed with local numbing for comfort. Most patients describe mild warmth rather
              than pain, and resume normal daily activity within hours. This is a non-surgical,
              in-office treatment with no hospital stay and minimal recovery time.
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border shadow-soft self-start">
            <img
              src={cgVaginalTightening.url}
              alt="Laser vaginal tightening procedure at Healic Multispeciality Hospital, Haridwar"
              width={780}
              height={520}
              loading="lazy"
              decoding="async"
              className="h-full max-h-[340px] w-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function HaridwarPriceFactors() {
  return (
    <>
      <p>
        Vaginal tightening treatment isn&apos;t one-size-fits-all, and neither is the price. At
        Healic, the vaginal tightening surgery cost quoted upfront already reflects a standard
        session — anything additional is discussed and priced before, not after.
      </p>
      <p>What actually shapes your final number:</p>
      <ul className="ml-5 list-disc space-y-2">
        <li>
          Whether one laser session is enough, or your case benefits from two to three sessions of
          vaginal tightening therapy
        </li>
        <li>Degree of laxity and whether maintenance sessions are advised</li>
        <li>Whether any supportive care is recommended alongside laser treatment</li>
      </ul>
    </>
  );
}

function HaridwarLaserSections() {
  return (
    <section className="bg-background py-12 lg:py-16">
      <div className="mx-auto max-w-5xl px-4 lg:px-8">
        <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
          Finding a Gynecologist in Haridwar for This Procedure
        </h2>
        <div className="mt-3 space-y-4 text-sm text-muted-foreground lg:text-base">
          <p>
            Many women prefer to consult a gynecologist in Haridwar rather than travel to Delhi NCR
            for this kind of procedure — comfort and easy follow-up matter. Looking for the best
            gynecologist in Haridwar for a cosmetic procedure like this means asking specific
            questions: how many laser vaginal tightening procedures has this gynecologist actually
            performed, will you consult directly with them rather than a coordinator, and does the
            quoted price stay fixed once confirmed?
          </p>
          <p>
            At Healic, consultations are led directly by Dr. Deepika Yadav, a consultant gynecologist
            in Haridwar with 9+ years of dedicated experience in gynaecology and cosmetic gynaecology
            — not a general practice add-on.
          </p>
        </div>
      </div>
    </section>
  );
}

function HaridwarTrustSection() {
  const points = [
    "Fixed, written pricing removes the anxiety of an unclear final bill",
    "Direct access to Dr. Yadav, rather than a sales coordinator, comes up frequently as a reason patients choose Healic",
    "Having a gynecologist available locally in Haridwar — rather than traveling to Delhi NCR — makes follow-up care more practical",
  ];
  return (
    <section className="bg-muted/40">
      <div className="mx-auto max-w-5xl px-4 py-12 lg:px-8 lg:py-14">
        <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
          What Patients Consistently Tell Us
        </h2>
        <ul className="mt-8 grid gap-5 sm:grid-cols-3">
          {points.map((p) => (
            <li
              key={p}
              className="rounded-2xl border border-border bg-card p-5 text-[13px] leading-relaxed text-foreground/80 shadow-soft"
            >
              {p}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const HARIDWAR_LASER_FAQS = [
  {
    q: "How much does laser vaginal tightening cost in Haridwar?",
    a: "Packages start from ₹15,000, confirmed in writing before your procedure — never estimated after the fact.",
  },
  {
    q: "Who is the best gynecologist in Haridwar for this procedure?",
    a: "That depends on your specific needs, but look for a gynecologist with dedicated cosmetic gynaecology experience, not just general practice. Dr. Deepika Yadav has 9+ years focused on gynaecology, including laser vaginal tightening.",
  },
  {
    q: 'Is this the same as what\u2019s sometimes called "virgin tightening surgery"?',
    a: "It's occasionally searched under that term, though it isn't medically accurate. The procedure addresses vaginal tone and elasticity — often relevant after childbirth or with age — unrelated to virginity.",
  },
  {
    q: "What's the difference between vaginal tightening treatment and vaginal rejuvenation?",
    a: "Vaginal rejuvenation is the broader category, covering laser treatment and supportive care together. Vaginal tightening usually refers specifically to improving firmness and elasticity, most often via laser.",
  },
  {
    q: "Should I travel to Delhi NCR, or can I consult a gynecologist in Haridwar directly?",
    a: "Both are valid, but many patients prefer a Haridwar-based consultation for easier follow-up. Consultations at Healic are led directly by Dr. Deepika Yadav, a consultant gynecologist based in Haridwar.",
  },
  {
    q: "Is insurance accepted?",
    a: "Cashless insurance applies where medically indicated. 0% EMI is also available.",
  },
  {
    q: "How long is recovery after laser vaginal tightening treatment?",
    a: "Same-day for most patients — normal activity resumes within hours.",
  },
  {
    q: "How long do results last?",
    a: "Laser results typically last 12–18 months, extendable with a maintenance session.",
  },
];

function MedicalDisclaimer() {
  return (
    <section className="bg-background pb-14">
      <div className="mx-auto max-w-5xl px-4 lg:px-8">
        <div className="rounded-2xl border border-border bg-muted/40 p-5 lg:p-6">
          <h2 className="font-display text-lg font-semibold text-foreground">Medical Disclaimer</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            The information provided on this page is intended for educational purposes only and
            should not be considered a substitute for professional medical advice, diagnosis, or
            treatment. Every woman&apos;s condition is unique, and the most appropriate vaginal
            tightening treatment, laser vaginal tightening procedure, or vaginal rejuvenation option
            should be determined after consultation with a qualified cosmetic gynecologist. Results
            vary based on individual medical conditions, age, lifestyle, and adherence to
            post-treatment care. If you experience symptoms related to vaginal health or have
            questions about laser vaginal tightening or vaginal rejuvenation, consult an experienced
            specialist at Healic for a comprehensive evaluation.
          </p>
        </div>
      </div>
    </section>
  );
}

function HubLinkSection() {
  return (
    <section className="bg-cream py-12">
      <div className="mx-auto max-w-5xl px-4 lg:px-8">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-soft lg:p-8">
          <h2 className="font-display text-2xl font-bold">
            Compare all vaginal tightening options and costs across India
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Our complete guide covers laser vaginal tightening — how it works, who it suits, recovery timelines and a full India-wide price comparison.
          </p>
          <a
            href="https://healic.in/cosmetic-gynecology/vaginal-tightening"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground"
          >
            Read the full vaginal tightening guide →
          </a>
        </div>
      </div>
    </section>
  );
}
